// Google Analytics 4 (gtag.js) loader. Idempotent: calling load() twice is safe.
// Only invoked after the user has granted consent.
//
// Page URLs are scrubbed (redact.ts): gtag would otherwise record
// document.location, which holds order ids on the order pages. page_location
// and page_referrer are set to the redacted URL at config time and again on
// every SPA route change (syncGaPage).

import { redactUrl } from './redact'

const DEFAULT_GA4_ID = 'G-GT2VKJ3NG3'
const SCRIPT_ATTR = 'data-helin-ga'

interface GtagWindow extends Window {
  dataLayer?: Array<unknown>
  gtag?: (...args: unknown[]) => void
}

export function getGa4Id(): string {
  const fromEnv = (import.meta.env.VITE_GA4_ID as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_GA4_ID
}

export function isGa4Loaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

export function loadGa4(id: string = getGa4Id()): void {
  if (isGa4Loaded()) return
  if (!id) return

  const w = window as GtagWindow
  w.dataLayer = w.dataLayer ?? []
  // gtag forwards every call as an arguments-list push onto dataLayer; this
  // matches the snippet Google ships and is what GA expects.
  w.gtag =
    w.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments)
    }
  const page = {
    page_location: redactUrl(window.location.href),
    page_referrer: redactUrl(document.referrer),
  }
  w.gtag('js', new Date())
  // `set` makes the redacted page apply to every later hit, `config` to the
  // first page view it sends.
  w.gtag('set', page)
  w.gtag('config', id, page)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  script.setAttribute(SCRIPT_ATTR, id)
  document.head.appendChild(script)
}

/**
 * SPA route change: point GA4 at the new (redacted) page and send its page
 * view. `referrer` is the previous in-app URL. No-op until GA4 is loaded.
 */
export function syncGaPage(url: string, referrer: string): void {
  const w = window as GtagWindow
  if (!w.gtag || !isGa4Loaded()) return
  const page = { page_location: redactUrl(url), page_referrer: redactUrl(referrer) }
  w.gtag('set', page)
  w.gtag('event', 'page_view', page)
}
